import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { 
  User, 
  Mail, 
  Lock, 
  Phone, 
  MapPin, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle,
  Edit,
  Save,
  X
} from 'lucide-react';

interface LoginFormData {
  email: string;
  password: string;
}

interface RegisterFormData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface AddressFormData {
  full_name: string;
  phone: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  pin_code: string;
  country: string;
}

const CustomerLoginPage: React.FC = () => {
  const { user, customer, loading, signIn, signUp, signOut, updateProfile } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    document.title = user ? "My Profile | Strum Music" : "Login | Strum Music";
  }, [user]);

  // Login form
  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
    reset: resetLogin
  } = useForm<LoginFormData>();

  // Registration form
  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    formState: { errors: signupErrors },
    watch: watchSignup,
    reset: resetSignup
  } = useForm<RegisterFormData>();

  // Address form
  const {
    register: registerAddress,
    handleSubmit: handleAddressSubmit,
    formState: { errors: addressErrors },
    reset: resetAddress,
    setValue: setAddressValue
  } = useForm<AddressFormData>({
    defaultValues: {
      country: 'India'
    }
  });

  // Watch password for confirmation validation
  const password = watchSignup('password');

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Populate address form when customer data is available
  useEffect(() => {
    if (customer && !isEditingAddress) {
      setAddressValue('full_name', customer.full_name || '');
      setAddressValue('phone', customer.phone || '');
      setAddressValue('address_line_1', customer.address_line_1 || '');
      setAddressValue('address_line_2', customer.address_line_2 || '');
      setAddressValue('city', customer.city || '');
      setAddressValue('state', customer.state || '');
      setAddressValue('pin_code', customer.pin_code || '');
      setAddressValue('country', customer.country || 'India');
    }
  }, [customer, setAddressValue, isEditingAddress]);

  const handleLogin = async (data: LoginFormData) => {
    setAuthLoading(true);
    setMessage(null);

    const { error } = await signIn(data.email, data.password);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Successfully logged in!' });
      resetLogin();
    }

    setAuthLoading(false);
  };

  const handleRegister = async (data: RegisterFormData) => {
    setAuthLoading(true);
    setMessage(null);

    const { error } = await signUp(data.email, data.password, data.fullName);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ 
        type: 'success', 
        text: 'Registration successful! Please check your email to verify your account.' 
      });
      resetSignup();
      setIsRegistering(false);
    }

    setAuthLoading(false);
  };

  const handleAddressUpdate = async (data: AddressFormData) => {
    setAuthLoading(true);
    setMessage(null);

    const { error } = await updateProfile(data);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Address updated successfully!' });
      setIsEditingAddress(false);
    }

    setAuthLoading(false);
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const cancelAddressEdit = () => {
    setIsEditingAddress(false);
    resetAddress();
    // Re-populate with current customer data
    if (customer) {
      setAddressValue('full_name', customer.full_name || '');
      setAddressValue('phone', customer.phone || '');
      setAddressValue('address_line_1', customer.address_line_1 || '');
      setAddressValue('address_line_2', customer.address_line_2 || '');
      setAddressValue('city', customer.city || '');
      setAddressValue('state', customer.state || '');
      setAddressValue('pin_code', customer.pin_code || '');
      setAddressValue('country', customer.country || 'India');
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is logged in, show profile page
  if (user) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container-custom max-w-4xl">
          {/* Profile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-sm p-6 mb-8"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-red-700" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Welcome, {customer?.full_name || user.email}!
                  </h1>
                  <p className="text-gray-600">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="btn btn-outline py-2 px-4"
              >
                Sign Out
              </button>
            </div>
          </motion.div>

          {/* Message Display */}
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>{message.text}</span>
            </motion.div>
          )}

          {/* Shipping Address Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <MapPin className="h-6 w-6 text-red-700" />
                <h2 className="text-xl font-bold text-gray-900">Shipping Address</h2>
              </div>
              {!isEditingAddress && (
                <button
                  onClick={() => setIsEditingAddress(true)}
                  className="btn btn-outline py-2 px-4 flex items-center space-x-2"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit</span>
                </button>
              )}
            </div>

            {!isEditingAddress ? (
              // Display Mode
              <div className="space-y-4">
                {customer?.full_name || customer?.phone || customer?.address_line_1 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Personal Information</h3>
                      <div className="space-y-2 text-gray-600">
                        <p><strong>Name:</strong> {customer.full_name || 'Not provided'}</p>
                        <p><strong>Phone:</strong> {customer.phone || 'Not provided'}</p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Address</h3>
                      <div className="space-y-1 text-gray-600">
                        {customer.address_line_1 && <p>{customer.address_line_1}</p>}
                        {customer.address_line_2 && <p>{customer.address_line_2}</p>}
                        {(customer.city || customer.state || customer.pin_code) && (
                          <p>
                            {[customer.city, customer.state, customer.pin_code].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {customer.country && <p>{customer.country}</p>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Address Added</h3>
                    <p className="text-gray-600 mb-4">Add your shipping address to make checkout faster.</p>
                    <button
                      onClick={() => setIsEditingAddress(true)}
                      className="btn btn-primary py-2 px-4"
                    >
                      Add Address
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Edit Mode
              <form onSubmit={handleAddressSubmit(handleAddressUpdate)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      {...registerAddress('full_name', { required: 'Full name is required' })}
                      className="input w-full"
                      placeholder="Enter your full name"
                    />
                    {addressErrors.full_name && (
                      <p className="text-red-600 text-sm mt-1">{addressErrors.full_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      {...registerAddress('phone', { required: 'Phone number is required' })}
                      className="input w-full"
                      placeholder="+91 98765 43210"
                    />
                    {addressErrors.phone && (
                      <p className="text-red-600 text-sm mt-1">{addressErrors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1 *
                  </label>
                  <input
                    {...registerAddress('address_line_1', { required: 'Address is required' })}
                    className="input w-full"
                    placeholder="House/Flat number, Building name, Street"
                  />
                  {addressErrors.address_line_1 && (
                    <p className="text-red-600 text-sm mt-1">{addressErrors.address_line_1.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    {...registerAddress('address_line_2')}
                    className="input w-full"
                    placeholder="Area, Landmark (Optional)"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <input
                      {...registerAddress('city', { required: 'City is required' })}
                      className="input w-full"
                      placeholder="City"
                    />
                    {addressErrors.city && (
                      <p className="text-red-600 text-sm mt-1">{addressErrors.city.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State *
                    </label>
                    <input
                      {...registerAddress('state', { required: 'State is required' })}
                      className="input w-full"
                      placeholder="State"
                    />
                    {addressErrors.state && (
                      <p className="text-red-600 text-sm mt-1">{addressErrors.state.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PIN Code *
                    </label>
                    <input
                      {...registerAddress('pin_code', { required: 'PIN code is required' })}
                      className="input w-full"
                      placeholder="110001"
                    />
                    {addressErrors.pin_code && (
                      <p className="text-red-600 text-sm mt-1">{addressErrors.pin_code.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <select
                    {...registerAddress('country', { required: 'Country is required' })}
                    className="input w-full"
                  >
                    <option value="India">India</option>
                    <option value="USA">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={authLoading}
                    className="btn btn-primary py-2 px-6 flex items-center space-x-2"
                  >
                    {authLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save Address</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelAddressEdit}
                    className="btn btn-outline py-2 px-6 flex items-center space-x-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Login/Registration page for non-authenticated users
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-sm p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="h-8 w-8 text-red-700" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isRegistering ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isRegistering 
                ? 'Join Strum Music to manage your orders and profile' 
                : 'Sign in to your Strum Music account'
              }
            </p>
          </div>

          {/* Message Display */}
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>{message.text}</span>
            </motion.div>
          )}

          {!isRegistering ? (
            // Login Form
            <form onSubmit={handleLoginSubmit(handleLogin)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    {...registerLogin('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="input w-full pl-10"
                    placeholder="your@email.com"
                  />
                </div>
                {loginErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{loginErrors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerLogin('password', { required: 'Password is required' })}
                    className="input w-full pl-10 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {loginErrors.password && (
                  <p className="text-red-600 text-sm mt-1">{loginErrors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="btn btn-primary w-full py-3 flex items-center justify-center space-x-2"
              >
                {authLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>

              <div className="text-center">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsRegistering(true)}
                    className="text-red-700 hover:text-red-800 font-medium"
                  >
                    Register
                  </button>
                </p>
              </div>
            </form>
          ) : (
            // Registration Form
            <form onSubmit={handleSignupSubmit(handleRegister)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    {...registerSignup('fullName', { required: 'Full name is required' })}
                    className="input w-full pl-10"
                    placeholder="John Doe"
                  />
                </div>
                {signupErrors.fullName && (
                  <p className="text-red-600 text-sm mt-1">{signupErrors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    {...registerSignup('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="input w-full pl-10"
                    placeholder="your@email.com"
                  />
                </div>
                {signupErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{signupErrors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    {...registerSignup('phone', { required: 'Phone number is required' })}
                    className="input w-full pl-10"
                    placeholder="+91 98765 43210"
                  />
                </div>
                {signupErrors.phone && (
                  <p className="text-red-600 text-sm mt-1">{signupErrors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerSignup('password', { 
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    className="input w-full pl-10 pr-10"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {signupErrors.password && (
                  <p className="text-red-600 text-sm mt-1">{signupErrors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...registerSignup('confirmPassword', { 
                      required: 'Please confirm your password',
                      validate: value => value === password || 'Passwords do not match'
                    })}
                    className="input w-full pl-10 pr-10"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {signupErrors.confirmPassword && (
                  <p className="text-red-600 text-sm mt-1">{signupErrors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="btn btn-primary w-full py-3 flex items-center justify-center space-x-2"
              >
                {authLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating account...</span>
                  </>
                ) : (
                  <span>Create Account</span>
                )}
              </button>

              <div className="text-center">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setIsRegistering(false)}
                    className="text-red-700 hover:text-red-800 font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerLoginPage;